<template>
    <validation-observer v-slot="{ passes }">
        <h1 class="title">{{ $t('title.beneficiaries') }}</h1>
        <div class="table-container">
            <b-table
                :backend-sorting="true"
                :backend-pagination="true"
                :hoverable="true"
                :striped="true"
                :paginated="true"
                :scrollable="true"
                :sticky-header="true"
                :current-page="local.parameters.page"
                :data="local.rows"
                :default-sort="[local.parameters.sortField, local.parameters.sortOrder]"
                :loading="local.loading"
                :pagination-position="'top'"
                :per-page="local.parameters.pageSize"
                :selected.sync="local.selected"
                :total="local.total"
                @page-change="onPageChange"
                @sort="onSortChange"
                @click="onRowClick"
            >
                <template #empty>
                    <section class="section">
                        <div class="content has-text-grey has-text-centered">
                            <h3 v-if="!local.loading">{{ $t('title.noResults') }}</h3>
                        </div>
                    </section>
                </template>

                <template #top-left>
                    <div class="content">
                        <h6>{{ $t('field.total') }}: {{ local.total }}</h6>
                    </div>
                </template>

                <b-table-column v-slot="props" field="id" width="5%">
                    <b-button size="is-small" type="is-primary" @click="detail(props.row.id)"
                        >{{ $t('button.detail') }}
                    </b-button>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="firstName"
                    :label="String($t('field.firstName')).toString()"
                    :sortable="true"
                >
                    {{ props.row.firstName }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="lastName"
                    :label="String($t('field.lastName')).toString()"
                    :sortable="true"
                >
                    {{ props.row.lastName }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="coach"
                    :label="String($t('field.coach')).toString()"
                    :sortable="true"
                >
                    {{ getCoachLabel(props.row.coachId) }}
                </b-table-column>
                <b-table-column v-slot="props" field="phoneNumber" :label="String($t('field.phoneNumber')).toString()">
                    {{ props.row.phoneNumber }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="bankSessions"
                    :label="String($t('field.bankSessions')).toString()"
                    :sortable="true"
                >
                    {{ props.row.bankSessions }}
                </b-table-column>
            </b-table>
        </div>
    </validation-observer>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';

import SelectField from '../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../framework/state';
import { getResourcePaging, getResources } from '../../../../framework/client/resource';
import { SortOrder } from '../../../../../common/framework/model/SortOrder';
import { QueryParameters } from '../../../../../common/application/model/QueryParameters';
import {
    getQueryParameters,
    getQueryParametersForBackend,
    setQueryParameter,
    setQueryParameters,
} from '../../../service/query_parameter_service';
import { FrameworkUserRole } from '../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import { Session } from '../../../../../common/application/model/session';
import SwitchField from '../../../../framework/fields/SwitchField.vue';
import { P_SORT_FIELD, P_SORT_ORDER } from '../../../../../common/framework/constants';
import { Customer } from '../../../../../common/application/model/customer';
import { getResourceLabels } from '../../../../framework/service/label_service';
import { Coach } from '../../../../../common/application/model/coach';
import { getCoachResourceLabel } from '../../../service/helper_service';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
        SwitchField,
        ValidationObserver,
    },
})
export default class CustomerBeneficiaries extends Vue {
    @Prop(String) readonly invoicingCustomerId!: string;

    readonly resourceType = ApplicationResource.CUSTOMER;

    shared = sharedState;
    local = {
        rows: new Array<Customer>(),
        total: 0,
        loading: false,
        selected: undefined as Customer | undefined,
        parameters: {} as QueryParameters,
        coachLabels: new Map<string, string>(),
    };

    async mounted() {
        this.local.loading = true;

        this.local.parameters = getQueryParameters(this);
        await this.loadAsyncData();

        this.local.loading = false;
    }

    detail(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}`);
        }
    }

    async loadAsyncData() {
        const parameters = getQueryParametersForBackend(this);
        parameters.set('invoicingCustomerId', this.invoicingCustomerId);
        parameters.set(P_SORT_FIELD, 'startDate');
        parameters.set(P_SORT_ORDER, SortOrder.ASC);

        this.local.total = (await getResourcePaging(this.resourceType, parameters)).rowCount;

        this.local.rows = [];

        const rows: Customer[] = await getResources(this.resourceType, this.local.parameters.page - 1, parameters);

        rows.filter((row) => row.id !== this.invoicingCustomerId).forEach((row) => {
            this.local.rows.push(row);
        });

        await this.getCoachLabels(rows);

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }
    }

    onSortChange(field: string, order: 'desc' | 'asc') {
        const parameters: QueryParameters = {
            ...this.local.parameters,
            sortField: field,
            sortOrder: order as SortOrder,
            page: 1,
        };

        setQueryParameters(this, parameters);
        this.local.parameters = getQueryParameters(this);
    }

    onPageChange(page: number) {
        setQueryParameter(this, 'page', page.toString());
        this.local.parameters = getQueryParameters(this);
    }

    get isAdmin(): boolean {
        return this.shared.hasRole(FrameworkUserRole.ADMIN);
    }

    onRowClick(row: Session): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    getCoachLabel(id: string): string | undefined {
        return this.local.coachLabels.get(id);
    }

    private async getCoachLabels(rows: Customer[]) {
        this.local.coachLabels = await getResourceLabels<Coach>(
            rows,
            'coachId',
            ApplicationResource.COACH,
            getCoachResourceLabel,
        );
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.table-container {
    overflow-y: auto;
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
</style>
